import { defineStore } from 'pinia'

import type OrganizationType from '~/enums/organization-type'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import type { AddressSuggestionData, OrganizationSuggestionData, BankSuggestionData } from '~/types/dadata'

export const messages = {
  failedToGetAddressSuggestions: 'Не удалось получить адреса.',
  failedToGetBankSuggestions: 'Не удалось получить информацию о банке.',
  failedToGetOrganizationSuggestions: 'Не удалось получить информацию о компании.'
}

type State = {
  addressSuggestions: AddressSuggestionData[]
  bankSuggestions: BankSuggestionData[]
  organizationSuggestions: OrganizationSuggestionData[]
}

const initialState = (): State => ({
  addressSuggestions: [],
  bankSuggestions: [],
  organizationSuggestions: []
})

export const useDadataStore = defineStore('dadata', {
  state: () => initialState(),
  getters: {
    getAddressSuggestions: (state: State) => state.addressSuggestions,
    getBankSuggestions: (state: State) => state.bankSuggestions,
    getOrganizationSuggestions: (state: State) => state.organizationSuggestions
  },
  actions: {
    clearAddressSuggestions (): void {
      this.addressSuggestions = []
    },
    clearBankSuggestions (): void {
      this.bankSuggestions = []
    },
    clearOrganizationSuggestions (): void {
      this.organizationSuggestions = []
    },
    dispose (): void {
      this.clearAddressSuggestions()
      this.clearBankSuggestions()
      this.clearOrganizationSuggestions()
    },
    async fetchAddressSuggestions (query: string): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()
      const filter = helpers.getParams([{ name: 'filter[query]', value: query, condition: query }])

      try {
        const response = await $apiHelper.addressSuggestions.getAddressSuggestions(filter) as any
        const { data } = JsonApiHelper.denormalizeResponse(response) as any

        this.addressSuggestions = data
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToGetAddressSuggestions)
      }
    },
    async fetchBankSuggestions (kpp: string|number): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()
      const filter = helpers.getParams([{ name: 'filter[query]', value: kpp, condition: kpp }])

      try {
        const response = await $apiHelper.bankSuggestions.getBankSuggestions(filter) as any
        const { data } = JsonApiHelper.denormalizeResponse(response) as any

        this.bankSuggestions = data
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToGetBankSuggestions)
      }
    },
    async fetchOrganizationSuggestions (tin: string|number, type: OrganizationType): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()
      const filter = helpers.getParams([
          { name: 'filter[query]', value: tin, condition: tin },
          { name: 'filter[type]', value: type, condition: type }
        ]
      )

      try {
        const response = await $apiHelper.organizationSuggestions.getOrganizationSuggestions(filter) as any
        const { data } = JsonApiHelper.denormalizeResponse(response) as any

        this.organizationSuggestions = data
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToGetOrganizationSuggestions)
      }
    }
  }
})
